'use client';

import { type ReactElement } from 'react';
import { useMediaQuery } from 'react-responsive';
import { cn } from '@fshr/shared-react';
import { useTranslations } from '@fshr/context/i18n-context';
import Icon from '../icon';

type Props = {
	count: number;
	index: number;
	fill: number;
	isPlaying: boolean;
	onTogglePlayback: () => void;
	onSelect: (index: number) => void;
};

export default function SlideshowProgress({
	count,
	index,
	fill,
	isPlaying,
	onTogglePlayback,
	onSelect,
}: Props): ReactElement {
	const translate = useTranslations();
	const isMobile = useMediaQuery({ maxWidth: 767 });

	return (
		// Figma "Slideshow progress bar" itemSpacing is 10px mobile / 15px
		// desktop — exact match for inline-s.
		<div className="flex items-center gap-x-inline-s">
			<button
				type="button"
				onClick={onTogglePlayback}
				aria-label={isPlaying ? translate('Pause slideshow') : translate('Play slideshow')}
				className="flex items-center justify-center text-text-heading focus-visible:outline-2 focus-visible:outline-offset-2"
			>
				<Icon
					icon={isPlaying ? 'pause_button_small' : 'play_button_small'}
					className="text-[15px]"
				/>
			</button>

			{/* Figma "progress bar" (the dots themselves) itemSpacing is 4px
			    mobile / 6px desktop — exact match for inline-2xs. */}
			<div className="flex items-center gap-x-inline-2xs">
				{Array.from({ length: count }, (_, slide) => {
					const isActive = slide === index;
					const track = cn(
						'relative h-[7px] overflow-hidden rounded-full bg-border-subtle',
						isActive ? 'w-500' : 'w-[7px]'
					);

					if (isMobile) {
						return (
							<span key={slide} className={track} aria-hidden="true">
								{isActive && (
									<span
										className="absolute inset-y-0 left-0 rounded-full bg-border-strong"
										style={{ width: `${fill * 100}%` }}
									/>
								)}
							</span>
						);
					}

					return (
						<button
							key={slide}
							type="button"
							onClick={() => onSelect(slide)}
							aria-label={translate('Go to slide {0} of {1}', [slide + 1, count])}
							aria-current={isActive}
							className={cn(track, 'focus-visible:outline-2 focus-visible:outline-offset-2')}
						>
							{isActive && (
								<span
									className="absolute inset-y-0 left-0 rounded-full bg-border-strong"
									style={{ width: `${fill * 100}%` }}
								/>
							)}
						</button>
					);
				})}
			</div>
		</div>
	);
}
